import {
  Airplane,
  CircleDashed,
  Smiley,
  SmileyMeh,
  SmileySad,
  ThumbsDown,
  ThumbsUp,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import {
  badgeDotColors,
  badgeVariantBorderStyles,
  badgeVariantIconTint,
  badgeVariantStyles,
  type BadgeVariant,
} from './utils'

/**
 * The reply-sentiment map (§10.1) — the ONE place a sentiment's label, glyph,
 * chip tone and chart hue are decided, shared by the Inbox and every Analytics
 * tab.
 *
 * Before this module the labels were reconciled but the colours were not:
 * `neutral` was muted gray in the Inbox chip and amber in RepliesTab while
 * OverviewTab had already ruled it indigo; `interested` was byte-identical to
 * `positive` in the Inbox while both Analytics tabs deliberately split them;
 * and the Inbox aliased the backend's `out_of_office` to the label
 * "Unanalyzed", which Analytics uses for a *different* bucket (NULL sentiment)
 * — so the two screens' "Unanalyzed" counts could never agree.
 *
 * Vocabulary is the backend's (`services/sentiment_analysis.py`): positive,
 * interested, neutral, negative, not_interested, out_of_office. `unanalyzed` is
 * not a stored value — it is a NULL sentiment, counted separately by
 * `/inbox/stats` (`unanalyzed`) and by the analytics breakdown (the NULL group).
 * It lives here because both screens must render it the same way.
 *
 * Tone follows §31: valence-positive is emerald, valence-negative is red,
 * `neutral` is brand indigo (see the ruling below), and the two "no human
 * sentiment" buckets are muted. Colour is never invented here — the chip
 * classes come from the badge tone tables in `lib/utils`, which stay the single
 * tone authority. This module adds only the label, the glyph and the chart hue.
 *
 * ### Ruling: `neutral` is indigo, not amber
 * OverviewTab had already made this call with its reasoning in a comment —
 * amber is reserved for security/trust signals (§3), so it is not a data hue,
 * and neutral is usually the dominant slice, which anchors better to the page's
 * indigo family than to a traffic-light island. RepliesTab's amber `neutral`
 * was the outlier and is retired. This deliberately does not touch R3 (amber as
 * a *series* hue elsewhere) — that is Stage 11's ruling to make.
 *
 * ### Ruling: `positive` and `interested` share a tone, not a hue
 * Both are positive valence, so both take the emerald chip tone — a reader who
 * learns "green = good news" is never contradicted. They are told apart by
 * label and glyph in chips, and by two steps of the same emerald family
 * (`#10b981` / `#34d399`) in charts, which is what keeps the Analytics split
 * meaningful instead of arbitrary.
 */
export type SentimentKey =
  | 'positive'
  | 'interested'
  | 'neutral'
  | 'negative'
  | 'not_interested'
  | 'out_of_office'
  | 'unanalyzed'

interface SentimentDefinition {
  label: string
  variant: BadgeVariant
  Icon: PhosphorIcon
  /** Series hue for recharts. Same value in both themes (§chartTheme). */
  chartColor: string
}

const SENTIMENT_META: Record<SentimentKey, SentimentDefinition> = {
  positive: {
    label: 'Positive',
    variant: 'success',
    Icon: Smiley,
    chartColor: '#10b981', // emerald — positive valence (success token)
  },
  interested: {
    label: 'Interested',
    variant: 'success',
    Icon: ThumbsUp,
    chartColor: '#34d399', // light emerald — engaged-positive, same family as positive
  },
  neutral: {
    label: 'Neutral',
    variant: 'info',
    Icon: SmileyMeh,
    chartColor: '#5e6ad2', // brand indigo — the dominant slice anchors to the page palette
  },
  negative: {
    label: 'Negative',
    variant: 'destructive',
    Icon: SmileySad,
    chartColor: '#ef4444', // red — negative valence (error token)
  },
  not_interested: {
    label: 'Not interested',
    variant: 'destructive',
    Icon: ThumbsDown,
    chartColor: '#f87171', // light red — same family as negative
  },
  // A real LLM-detected auto-responder category, and NOT the same thing as
  // "not yet classified" — it says the person never answered. Analytics
  // excludes it from the sentiment charts for exactly that reason; the Inbox
  // shows it, labelled for what it is.
  out_of_office: {
    label: 'Out of office',
    variant: 'default',
    Icon: Airplane,
    chartColor: '#8a8f98', // tertiary gray — meta bucket, no valence
  },
  // NULL sentiment. Muted like out_of_office (neither carries valence) but a
  // distinctly darker gray in charts so the two never blur into one slice.
  unanalyzed: {
    label: 'Unanalyzed',
    variant: 'default',
    Icon: CircleDashed,
    chartColor: '#62666d', // quaternary — not yet classified
  },
}

/**
 * Canonical render order for legends, dropdowns, stat rows and pie slices:
 * positive valence → neutral → negative valence → the two meta buckets. Keeps
 * the two grays (`out_of_office`, `unanalyzed`) apart from `neutral` so no two
 * adjacent slices are hard to separate, and means every surface lists
 * sentiments in the same sequence.
 */
export const SENTIMENT_ORDER: SentimentKey[] = [
  'positive',
  'interested',
  'neutral',
  'negative',
  'not_interested',
  'out_of_office',
  'unanalyzed',
]

/** The six values the backend actually stores — `unanalyzed` is a NULL, not a value. */
export const STORED_SENTIMENT_KEYS: SentimentKey[] = SENTIMENT_ORDER.filter(
  (k) => k !== 'unanalyzed',
)

export function isSentimentKey(value: string): value is SentimentKey {
  return value in SENTIMENT_META
}

/** "meeting_request" → "Meeting request", for a value the backend adds later. */
function humanizeSentiment(value: string): string {
  const spaced = value.replace(/[_-]+/g, ' ').trim().toLowerCase()
  if (!spaced) return 'Unknown'
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export interface SentimentMeta {
  /** The matched sentiment, or `'unknown'` for anything the API adds later. */
  key: SentimentKey | 'unknown'
  label: string
  variant: BadgeVariant
  Icon: PhosphorIcon
  /** Soft chip classes (bg + text), no border. */
  chipClass: string
  /** Soft chip classes with a matching border, for pills on a card surface. */
  borderChipClass: string
  /** Status dot. */
  dotClass: string
  /** Icon-chip tint: `bg` for the chip, `fg` for the glyph. */
  iconTint: { bg: string; fg: string }
  /** Series hue for recharts. */
  chartColor: string
}

/**
 * Everything a surface needs to render one sentiment. `null` / `undefined` /
 * empty resolves to `unanalyzed` — that is precisely what a missing sentiment
 * means. An unrecognised value degrades to a muted chip labelled with the
 * humanized raw value rather than silently borrowing another sentiment's
 * colour.
 */
export function sentimentMeta(sentiment?: string | null): SentimentMeta {
  const raw = (sentiment || '').toLowerCase().trim()
  const key: SentimentKey | 'unknown' = !raw
    ? 'unanalyzed'
    : isSentimentKey(raw)
      ? raw
      : 'unknown'
  const def = key === 'unknown' ? null : SENTIMENT_META[key]
  const variant: BadgeVariant = def ? def.variant : 'default'

  return {
    key,
    label: def ? def.label : humanizeSentiment(raw),
    variant,
    Icon: def ? def.Icon : CircleDashed,
    chipClass: badgeVariantStyles[variant],
    borderChipClass: badgeVariantBorderStyles[variant],
    dotClass: badgeDotColors[variant],
    iconTint: badgeVariantIconTint[variant],
    chartColor: def ? def.chartColor : SENTIMENT_META.unanalyzed.chartColor,
  }
}

/** Chart hue for a sentiment — unknown values fall back to the unanalyzed gray. */
export function sentimentChartColor(sentiment?: string | null): string {
  return sentimentMeta(sentiment).chartColor
}

/** Label for a sentiment, for callers that need nothing else. */
export function sentimentLabel(sentiment?: string | null): string {
  return sentimentMeta(sentiment).label
}

/**
 * Dropdown / legend options in canonical order. Pass the keys a surface can
 * actually offer — the Inbox filter, for instance, cannot offer `unanalyzed`
 * because the list endpoint matches the stored column and a NULL is not a
 * value it can be given.
 */
/** One doughnut slice: the count plus everything needed to draw and name it. */
export interface SentimentSlice {
  key: SentimentKey
  name: string
  value: number
  color: string
}

/**
 * Turn an API sentiment breakdown into doughnut slices.
 *
 * Ordered by SENTIMENT_ORDER rather than by the object's own key order, so
 * every surface lists the tones in the same sequence and the two grays stay
 * away from neutral. Keys the payload does not carry are skipped rather than
 * drawn as zero: a surface that does not report out_of_office should not grow
 * a legend entry for it.
 *
 * Shared because the Overview doughnut and the campaign detail doughnut plot
 * the same thing, and a second copy of this mapping is how one of them would
 * end up with a different colour or a different order.
 */
export function sentimentDonutData(counts: Partial<Record<SentimentKey, number>>): SentimentSlice[] {
  return SENTIMENT_ORDER.filter((key) => key in counts).map((key) => ({
    key,
    name: sentimentLabel(key),
    value: counts[key] ?? 0,
    color: sentimentChartColor(key),
  }))
}

export function sentimentOptions(
  keys: SentimentKey[] = STORED_SENTIMENT_KEYS,
): { value: SentimentKey; label: string }[] {
  return SENTIMENT_ORDER.filter((k) => keys.includes(k)).map((k) => ({
    value: k,
    label: SENTIMENT_META[k].label,
  }))
}
